<template>
  <AuthShell
    title="Register"
    subtitle="Create your Filmsomniac account."
  >
    <div class="auth-panel__section">
      <h2 class="auth-panel__title">Register with email</h2>
      <p class="auth-panel__helper">Start building your own watchlists, ratings, and calendars.</p>
    </div>

    <div v-if="errorMessage" ref="errorMessageSection" class="auth-panel__section">
      <div class="auth-error-message" data-testid="signup-error">{{ errorMessage }}</div>
    </div>

    <form class="auth-panel__section auth-form" @submit.prevent="onSubmit">
      <div class="auth-field">
        <label class="auth-label" for="signup-username">Username</label>
        <input
          id="signup-username"
          v-model="username"
          class="auth-input"
          data-testid="signup-username"
          type="text"
          required
          maxlength="150"
          autocomplete="username"
        >
      </div>

      <div class="auth-field">
        <label class="auth-label" for="signup-email">Email address</label>
        <input
          id="signup-email"
          v-model="email"
          class="auth-input"
          data-testid="signup-email"
          type="email"
          required
          autocomplete="email"
        >
      </div>

      <div class="auth-field">
        <label class="auth-label" for="signup-password">Password</label>
        <div class="auth-password-field">
          <input
            id="signup-password"
            v-model="password"
            class="auth-input auth-password-field__input"
            data-testid="signup-password"
            :type="showPassword ? 'text' : 'password'"
            required
            autocomplete="new-password"
          >
          <button
            class="auth-password-toggle"
            data-testid="signup-password-toggle"
            type="button"
            :aria-label="showPassword ? 'Hide password' : 'Show password'"
            @click="showPassword = !showPassword"
          >
            <i :class="showPassword ? 'ion-eye-disabled' : 'ion-eye'" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <div class="auth-field">
        <label class="auth-label" for="signup-password-confirm">Confirm password</label>
        <div class="auth-password-field">
          <input
            id="signup-password-confirm"
            v-model="passwordConfirm"
            class="auth-input auth-password-field__input"
            data-testid="signup-password-confirm"
            :type="showPasswordConfirm ? 'text' : 'password'"
            required
            autocomplete="new-password"
          >
          <button
            class="auth-password-toggle"
            data-testid="signup-password-confirm-toggle"
            type="button"
            :aria-label="showPasswordConfirm ? 'Hide confirm password' : 'Show confirm password'"
            @click="showPasswordConfirm = !showPasswordConfirm"
          >
            <i :class="showPasswordConfirm ? 'ion-eye-disabled' : 'ion-eye'" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <p class="auth-terms">
        By registering, I confirm that I am at least 13 years old and I accept the
        <a class="auth-link" data-testid="signup-terms-link" href="/tos">Terms of Service</a>.
      </p>

      <div class="auth-actions">
        <button
          class="auth-submit"
          data-testid="signup-submit"
          type="submit"
          :disabled="isSubmitting"
        >
          {{ isSubmitting ? 'Registering…' : 'Register' }}
        </button>
        <p class="auth-secondary">
          Already have an account?
          <a class="auth-link" href="/login">Login</a>
        </p>
      </div>
    </form>

    <div v-if="showGoogleLogin" class="auth-panel__section auth-divider">
      <span>Or register with</span>
    </div>

    <div v-if="showGoogleLogin" class="auth-panel__section">
      <div class="auth-social-grid">
        <a class="auth-social-link" :href="googleLoginPath">
          <i class="icon-google" aria-hidden="true"></i>
          <span>Google</span>
        </a>
      </div>
    </div>

    <div v-if="showSuccess" class="auth-modal-backdrop" role="dialog" aria-modal="true">
      <div class="auth-modal-card">
        <h4>Registration was successful.</h4>
        <p>You can proceed to log in after confirming your email.</p>
        <button
          class="auth-submit"
          data-testid="signup-success-close"
          @click="goToLogin"
        >
          OK
        </button>
      </div>
    </div>
  </AuthShell>
</template>

<script setup>
import { nextTick, ref } from 'vue'
import AuthShell from '../components/AuthShell.vue'
import { buildGoogleLoginPath } from '../utils/auth'

const username = ref('')
const email = ref('')
const password = ref('')
const passwordConfirm = ref('')
const showPassword = ref(false)
const showPasswordConfirm = ref(false)
const isSubmitting = ref(false)
const errorMessage = ref('')
const errorMessageSection = ref(null)
const showSuccess = ref(false)
const showGoogleLogin = true
const googleLoginPath = buildGoogleLoginPath('/home')

const resetMessages = () => {
  errorMessage.value = ''
  showSuccess.value = false
}

const goToLogin = () => {
  window.location.assign('/login')
}

const showSignupError = async (message) => {
  errorMessage.value = message
  await nextTick()
  if (typeof errorMessageSection.value?.scrollIntoView === 'function') {
    errorMessageSection.value.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
}

const onSubmit = async () => {
  resetMessages()
  if (password.value !== passwordConfirm.value) {
    await showSignupError('Passwords do not match.')
    return
  }
  isSubmitting.value = true
  try {
    const resp = await fetch('/api/v1/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: username.value,
        email: email.value,
        password: password.value,
        password_confirm: passwordConfirm.value,
      }),
    })
    if (!resp.ok) {
      const data = await resp.json().catch(() => ({}))
      const firstError = typeof data === 'object' ? Object.values(data)[0] : null
      await showSignupError(
        Array.isArray(firstError) ? firstError[0] : firstError || 'Signup failed.',
      )
      return
    }
    showSuccess.value = true
  } catch (e) {
    await showSignupError('Network error. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}
</script>
